import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useState } from "react";

import {
  CODE_COLORS,
  CODE_GROUND,
  CODE_GUTTER_INK,
  tokenizeLine,
} from "@/components/marketing/codeSample";
import {
  BODY,
  BODY_SMALL,
  HEADING_2,
} from "@/components/marketing/marketingType";
import {
  ACCENT_DEEP,
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Laid } from "@/content/landing/sectionLayout";
import { useNeighbours } from "../LandingPageContext";
import { LandingButton, LandingSection } from "../LandingPrimitives";
import { Lines } from "@/components/marketing/Lines";

type Code = Laid<"code">;

/** A zero-width space: a line with no text node collapses. */
const EMPTY_LINE = "​";

/**
 * `.eth-sourc_wrap`'s own margins. The published block opens on a longer break
 * than a section does and closes on the section's own.
 */
const SECTION_BREAK = {
  pt: "112px",
  pb: "100px",
  [NOT_WIDE]: { pt: "80px" },
  [TINY]: { pb: "64px" },
};

/**
 * The same, over the benefits band: `benefits_wrap` opens on a 100 of its
 * own (64 below the wide step, 48 on a phone), which adds to the close.
 */
const SECTION_BREAK_OVER_BENEFITS = {
  pt: "112px",
  pb: "200px",
  [NOT_WIDE]: { pt: "80px", pb: "164px" },
  [TINY]: { pb: "112px" },
};

function CodeLines({ code }: { code: string }) {
  const lines = code.split("\n");
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "29px minmax(0, 1fr)",
        // `.CodeMirror-lines`' own padding.
        py: "4px",
        fontFamily: "monospace",
        fontSize: 15,
        lineHeight: "22.5px",
        color: CODE_COLORS.plain,
        // The published editor runs with line wrapping on, so a long line takes
        // a second row rather than sliding out of the panel's right edge.
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
        // Set on the grid so the gutter numbers step with the lines they number.
        [MID]: { fontSize: 13, lineHeight: "19.5px" },
      }}
    >
      {lines.map((line, index) => (
        <Box key={index} sx={{ display: "contents" }}>
          {/* `.CodeMirror-linenumber`, which numbers from the right. */}
          <Box
            aria-hidden
            sx={{
              pl: "5px",
              pr: "3px",
              textAlign: "right",
              color: CODE_GUTTER_INK,
            }}
          >
            {index + 1}
          </Box>
          <Box
            component="pre"
            sx={{
              m: 0,
              px: "4px",
              fontFamily: "inherit",
              whiteSpace: "inherit",
              overflowWrap: "inherit",
            }}
          >
            {line.length === 0 ? (
              <Box component="span">{EMPTY_LINE}</Box>
            ) : (
              tokenizeLine(line).map((token, tokenIndex) => (
                <Box
                  component="span"
                  key={tokenIndex}
                  sx={{ color: CODE_COLORS[token.kind] }}
                >
                  {token.text}
                </Box>
              ))
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/** `.integration-left_content`: one flat 16px stack, left-aligned. */
function CodeCopy({ block }: { block: Code }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        gap: "16px",
      }}
    >
      {block.title ? (
        <Typography component="h2" sx={HEADING_2}>
          <Lines text={block.title} />
        </Typography>
      ) : null}
      {block.body ? (
        <Typography
          component="p"
          sx={block.bodyTone === "small" ? BODY_SMALL : BODY}
        >
          {block.body}
        </Typography>
      ) : null}
      {block.bullets?.length ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {block.bullets.map((bullet) => (
            <Box
              key={bullet}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10.08px",
                color: marketingPalette.ink,
                fontSize: 15,
                lineHeight: "22.5px",
                WebkitTextStrokeWidth: "0.5px",
              }}
            >
              <Box
                aria-hidden
                sx={{
                  flex: "0 0 auto",
                  width: "0.5em",
                  height: "0.5em",
                  mt: "8.48px",
                  borderRadius: "50%",
                  backgroundColor: ACCENT_DEEP,
                }}
              />
              <Box component="span">{bullet}</Box>
            </Box>
          ))}
        </Box>
      ) : null}
      {block.cta ? (
        <Box sx={{ display: "flex" }}>
          <LandingButton cta={block.cta} />
        </Box>
      ) : null}
    </Box>
  );
}

/** "Connect in minutes": copy on the left, tabbed connection snippets on the right. */
export default function CodeBlock({ block }: { block: Code }) {
  const [active, setActive] = useState(0);
  const tab = block.tabs[active] ?? block.tabs[0];
  const { next } = useNeighbours(block);
  return (
    <LandingSection
      padding="none"
      sx={next?.kind === "cards" ? SECTION_BREAK_OVER_BENEFITS : SECTION_BREAK}
    >
      {/* A narrower frame than the rest of the page: 1100, laid out as however
          many 31rem columns fit rather than as a pair that steps at a width.
          That is what takes the panel under the copy at 1000, where a
          two-column rule measured at 991 would still be side by side. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fill, minmax(min(100%, 496px), 1fr))",
          rowGap: "16px",
          columnGap: 0,
          alignItems: "center",
          maxWidth: "1100px",
          mx: "auto",
        }}
      >
        <CodeCopy block={block} />
        {/* `.integration-tab_box`: as tall as `.code-embed`'s 26rem inside
            the pane's 24 (12 below the card step), over the tabs' row. */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            borderRadius: "16px",
            backgroundColor: CODE_GROUND,
          }}
        >
          <Box
            sx={{
              position: "relative",
              p: "24px",
              [MID]: { p: "12px" },
            }}
          >
            {/* `auto`, not `hidden`: the code scrolls under the copy pill,
                which is placed over its bottom right. */}
            <Box
              sx={{
                position: "relative",
                height: "416px",
                overflow: "auto",
              }}
            >
              {tab ? <CodeLines code={tab.code} /> : null}
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => {
                if (tab) void navigator.clipboard?.writeText(tab.code);
              }}
              sx={{
                position: "absolute",
                right: "24px",
                bottom: "24px",
                [MID]: { right: "12px", bottom: "12px" },
                border: 0,
                cursor: "pointer",
                fontFamily: "inherit",
                px: "16px",
                height: "38px",
                borderRadius: "1440px",
                backgroundColor: marketingPalette.purple,
                color: "common.white",
                fontSize: 14.72,
                lineHeight: "22.08px",
                "&:hover": { backgroundColor: marketingPalette.purpleHover },
              }}
            >
              Copy Code
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: "6.08px",
              flexWrap: "wrap",
              p: "24px",
              borderTop: "1px solid rgba(255, 255, 255, 0.12)",
              // `.integration-tabs_list` below the card step: one row that scrolls.
              [MID]: { flexWrap: "nowrap", overflowX: "auto" },
            }}
          >
            {block.tabs.map((item, index) => (
              <Box
                component="button"
                type="button"
                key={item.label}
                onClick={() => setActive(index)}
                aria-pressed={index === active}
                sx={{
                  px: "16px",
                  py: "8px",
                  borderRadius: "1440px",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: 14,
                  lineHeight: "21px",
                  color: "common.white",
                  backgroundColor:
                    index === active
                      ? "rgba(255, 255, 255, 0.16)"
                      : "transparent",
                }}
              >
                {item.label}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </LandingSection>
  );
}
